import React, { useState } from "react";
import Joi from "joi";
import UploadFilesComp from "./UploadFilesComp";
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import Select, { SingleValue } from "react-select";
import LoadingIcon from "../../../../../../../common/LoadingIcon";
import TextAreaCustom from "../../../../../../../common/TextAreaCustom";
import useVendors from "../../../../../../../store/ReactQueries/procurements/vendors/useVendors";
import useQuotationRequests from "../../../../../../../store/ReactQueries/procurements/quotationRequests/useQuotationRequests";
import useAddQuotationRequest from "../../../../../../../store/ReactQueries/procurements/quotationRequests/useAddQuoatationRequest";
import useUpdateQuotationRequest from "../../../../../../../store/ReactQueries/procurements/quotationRequests/useUpdateQuotationRequest";
import { Modal } from '@mui/base/Modal';
import { ModeTypes } from "../AddPurchaseOrder";
import { Button, InputAdornment, TextField } from '@mui/material';
import { StyledBackdrop } from "../../../../../Users/NewAccModal";
import { FieldErrors } from "../../../../../../../domain/helpers/Errors";
import { validateSchema } from "../../../../../../../common/ValidateSchema";
import { useDarkModeStore } from "../../../../../../../store/useDarkModeStore";
import { useLanguageStore } from "../../../../../../../store/useLanguageStore";
import { ModalContent } from "../../../../../../../common/TopBar/Buttons/SearchTopBarModal";
import { darkModeBackgroud, darkModeBackgroudInputs } from "../../../../../../../utils/constants";
import { quotationFileSchema } from "../../../../../../../domain/ValidationSchemas/PurchaseOrder/quotationFile";
import { QuotationRequestInterface } from "../../../../../../../models/ProcurementModule/QuotationRequest";
import { quotationRequestSchema } from "../../../../../../../domain/ValidationSchemas/PurchaseOrder/quotationRequest";

interface AddQuotationRequestProps {
    purchaseOrderId: string | undefined;
    form: QuotationRequestInterface;
    mode: ModeTypes;
    open: {
        id: string;
        value: boolean;
    };
    setForm: React.Dispatch<React.SetStateAction<QuotationRequestInterface>>;
    handleQuotationModal: () => void;
    onClose: () => void;
}

const AddQuotationRequest: React.FC<AddQuotationRequestProps> = ({
    form, open, purchaseOrderId,
    handleQuotationModal, onClose, setForm
}) => {
    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();
    const { data: vendors } = useVendors(0, 0);
    const { data: quotationRequestsData } = useQuotationRequests(purchaseOrderId, 0, 0);

    const [errors, setErrors] = useState<FieldErrors>({});
    const [vendorTitle, setVendorTitle] = useState<string>(form?.vendor ? form.vendor.name : '');
    const [fileNames, setFileNames] = useState<{ fileUrl: string, fileName: string }[]>([]);
    const addQuotationRequest = useAddQuotationRequest(purchaseOrderId, 0, 0, fileNames);
    const updateQuotationReques = useUpdateQuotationRequest(purchaseOrderId, 0, 0, fileNames);

    const [disabledSave, setDisabledSave] = useState<boolean>(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value, id, type } = e.target;
        delete errors?.[id];
        setForm({ ...form, [id]: type === 'number' ? parseFloat(value) : value });
    }

    const newItemId = 'New-Quotation-Req-';

    const initialValue: QuotationRequestInterface = {
        ...form,
        id: `${newItemId}${Math.random()}`,
        price: 0,
        uploadFiles: [],
        vendor: { name: '' },
        vendorId: '',
        files: [],
        comment: '',
    };
    const handleSave = async () => {
        const addForm = form.id.includes(newItemId);

        let schemaToUse = quotationRequestSchema.keys({
            uploadFiles: Joi.array()
                .items(Joi.string().min(2).label("File"))
                .min(addForm ? 1 : 0)
                .when(Joi.ref('$addForm'), {
                    is: true,
                    then: Joi.required(),
                    otherwise: Joi.allow(null).empty(),
                })
                .label("Files"),
            files: Joi.array()
                .items(quotationFileSchema)
                .min(addForm ? 0 : 1)
                .when(Joi.ref('$addForm'), {
                    is: true,
                    then: Joi.allow(null).empty(),
                    otherwise: Joi.required(),
                })
                .label("Files"),
        });

        const isValid = validateSchema(schemaToUse, form);
        setErrors(isValid.errors);
        if (!isValid.result) return;

        setDisabledSave(true);

        try {
            if (addForm) {
                await new Promise((resolve, reject) => {
                    addQuotationRequest.mutate(form, {
                        onSuccess: resolve,
                        onError: reject,
                    });
                });
            } else {
                const upForm = { ...form };
                upForm.files = upForm.files ? upForm.files.filter(item => !item.id.includes('New-File-')) : [];
                await new Promise((resolve, reject) => {
                    updateQuotationReques.mutate(upForm, {
                        onSuccess: resolve,
                        onError: reject,
                    });
                });
            }

            // After the mutation is complete
            setDisabledSave(false); // Set to false after mutation is complete
            setForm(initialValue);
            onClose();
            handleQuotationModal();
        } catch (error) {
            // Handle any errors that occurred during mutation
            setDisabledSave(false); // Set to false if there was an error
            console.error("Error during save:", error);
        }
    };


    const closeModal = () => {
        onClose();
        handleQuotationModal();
        setForm(initialValue);
    };

    const handleSelect = (
        selectedOption: SingleValue<{
            label: any;
            value: string;
        }>,
    ) => {
        if (!selectedOption) {
            setForm({
                ...form,
                vendorId: "",
                vendor: { name: '' },
            });
            return;
        }

        setForm({
            ...form,
            vendorId: selectedOption.value,
            vendor: { name: selectedOption.label },
        });
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
        const { target } = e;
        let updatedValue = target.value;

        if (updatedValue.startsWith('0.')) { }
        else if (updatedValue === '0') updatedValue = '';
        else updatedValue = updatedValue.replace(/^0+(?!$)/, '');

        setForm((prevForm) => ({ ...prevForm, [target.id]: updatedValue }));
    };

    const vendorIds = quotationRequestsData?.map(item => item.vendorId);

    return (
        <Modal
            open={true}
            onClose={closeModal}
            slots={{ backdrop: StyledBackdrop }}
        >
            <ModalContent
                sx={{
                    width: 850,
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: darkMode ? darkModeBackgroud : 'white',
                    zIndex: 1300,
                }}
            >
                <div>
                    <div className="d-flex flex-row align-items-center ps-3 pe-3 pt-3">
                        <h4 className={`${darkMode ? 'text-white' : 'text-muted'} text-truncate`} style={{ maxWidth: 800 }}>
                            {language['AddQuotationRequest']}
                        </h4>
                        <IconButton
                            aria-label="Close Modal"
                            title="Close Modal"
                            size="small"
                            color="default"
                            className="ms-auto"
                            onClick={closeModal}
                        >
                            <ClearIcon />
                        </IconButton>
                    </div>

                    <hr className={`m-0 mt-2 p-0 ${darkMode && 'text-white opacity-100'}`} />

                    <div className="d-flex flex-row gap-3 w-100 p-4">
                        <div className="d-flex flex-column w-50 position-relative mt-2">
                            <label
                                title={language['Vendor'] ?? 'Vendor'}
                                id={'Vendor'}
                                htmlFor={'Vendor'}
                                className={`position-absolute ps-2 pe-2 ${darkMode ? 'text-white' : 'text-muted'}`}
                                style={{ top: -8, left: 10, zIndex: 12, fontSize: 12, background: darkMode ? darkModeBackgroudInputs : 'white', }}
                            >
                                {language['Vendor'] ?? 'Vendor'}
                            </label>
                            <Select
                                id='Vendor'
                                key='Vendor'
                                value={{
                                    label: `${form?.vendor?.name ?? ""}`,
                                    value: form?.vendorId || "",
                                }}
                                className={`${darkMode && "dark-mode-input"}`}
                                onChange={handleSelect}
                                onInputChange={(inputValue) => setVendorTitle(inputValue)}
                                placeholder="Zgjedh"
                                options={vendors
                                    ?.filter(({ id }) => id && !vendorIds?.includes(id))
                                    .map(({ id, name }) => ({
                                        value: id ?? "",
                                        label: `${name ?? ""}`,
                                    }))
                                }
                                isClearable
                                styles={{
                                    menu: (provided) => ({
                                        ...provided,
                                        zIndex: 11,
                                    }),
                                    container: (provided) => ({
                                        ...provided,
                                        position: "relative",
                                        zIndex: 11,
                                        color: darkMode ? 'white' : 'initial',
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        color: darkMode ? 'white' : 'black',
                                    }),
                                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                                    control: (provided) => ({
                                        ...provided,
                                        backgroundColor: darkMode ? darkModeBackgroudInputs : "#F3F6F9",
                                        borderColor: darkMode ? 'white' : "#DEE2E6",
                                        height: 56,
                                        background: darkMode ? darkModeBackgroudInputs : 'white',
                                    }),
                                }}
                                menuPlacement="bottom"
                                menuPortalTarget={document.body}
                            />
                        </div>
                        <TextField
                            id='price'
                            key='price'
                            label={language['Price']}
                            type='number'
                            variant="outlined"
                            className='mt-2 mb-2 w-50'
                            value={form.price}
                            onChange={(e) => handleChange(e)}
                            error={Boolean(errors?.['price'])}
                            helperText={errors?.['price'] || ''}
                            style={darkMode ? { background: darkModeBackgroud, color: 'white' } : {}}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" >
                                        €
                                    </InputAdornment>
                                ),
                                style: {
                                    color: darkMode ? 'white' : 'black',
                                    border: darkMode ? '1px solid #9E9EA1' : 'initial'
                                }
                            }}
                            InputLabelProps={{
                                style: {
                                    color: darkMode ? 'white' : '',
                                }
                            }}
                            onFocus={handleFocus}
                            onBlur={handleFocus}
                        />
                    </div>
                    <div className='position-relative ps-4 pe-4'>
                        <label
                            htmlFor='comment'
                            className='position-absolute ps-2 pe-2'
                            style={{
                                top: -8,
                                left: 35,
                                zIndex: 8,
                                fontSize: 12,
                                color: darkMode ? 'white' : 'initial',
                                background: darkMode ? darkModeBackgroudInputs : 'white',
                            }}
                        >
                            {language['Comment']}
                        </label>
                        <TextAreaCustom
                            errors={errors}
                            form={form}
                            name={'comment'}
                            setForm={setForm}
                            view={false}
                            className={` ${errors?.['comment'] && 'border-danger'}`}
                        />
                        {
                            errors?.['comment']
                            && <p
                                className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeMedium MuiFormHelperText-contained css-1wc848c-MuiFormHelperText-root"
                                style={{ color: 'red', marginTop: '0.5rem' }}
                            >
                                {errors?.['comment']}
                            </p>
                        }
                    </div>
                    {
                        <UploadFilesComp
                            mode="Add"
                            purchaseOrderId={purchaseOrderId}
                            open={open}
                            form={form}
                            setForm={setForm}
                            errors={errors}
                            fileNames={fileNames}
                            setFileNames={setFileNames}
                        />
                    }

                    <hr className={`m-0 p-0 ${darkMode && 'text-white opacity-100'}`} />

                    <div className="d-flex align-items-center justify-content-end p-2 mt-2 gap-2">
                        <Button
                            className="w-25 fw-bold"
                            sx={{
                                fontSize: "14px",
                                background: darkMode ? darkModeBackgroudInputs : "white",
                                border: `1px solid var(--btn-color)`,
                                color: darkMode ? 'white' : "var(--btn-color)",
                                "&:hover": {
                                    background: darkMode ? darkModeBackgroudInputs : "white",
                                    opacity: .7,
                                },
                            }}
                            onClick={closeModal}
                        >
                            {language["Cancel"]}
                        </Button>
                        <Button
                            className="w-25 fw-bold"
                            sx={{
                                background: "var(--btn-color)",
                                color: "white",
                                "&:hover": {
                                    background: "var(--btn-color)",
                                    opacity: .7
                                },
                            }}
                            onClick={handleSave}
                        >
                            {disabledSave
                                ? <LoadingIcon width={25} />
                                : language["Save"]
                            }
                        </Button>
                    </div>
                </div>
            </ModalContent>
        </Modal >
    );
};

export default AddQuotationRequest;
